import { ApiError } from "@nodus/api";
import { ActionLink, Button, Callout, Checkbox, Label, Select } from "@nodus/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { AuthCard } from "../../../app/auth-card";
import { useRuntime } from "../../../app/runtime";
import { useAuth } from "../../../auth/auth-context";
import { allowedBy, describeScope } from "../../../features/console/scopes";

// MCP OAuth consent (ui-spec §6.20, ADR-002, ADR-082): who is asking, where, what (only narrowable), how long.
export const Route = createFileRoute("/_authed/oauth/consent")({
  validateSearch: (s: Record<string, unknown>): { authorization_id?: string } =>
    typeof s.authorization_id === "string" ? { authorization_id: s.authorization_id } : {},
  component: Consent,
});

function Consent() {
  const { authorization_id: id = "" } = Route.useSearch();
  const { api, client } = useRuntime();
  const { session } = useAuth();
  const auth = useQuery({ queryKey: ["user", "agent-authorization", id], queryFn: () => api.agentAuthorization(id), retry: false, enabled: Boolean(id) });
  const orgs = useQuery({ queryKey: ["user", "orgs", session?.user.id], queryFn: api.organizations });
  const [org, setOrg] = React.useState<string>();
  const chosenOrg = org ?? orgs.data?.[0]?.metadata.name ?? "";
  const [projects, setProjects] = React.useState("all");
  const who = useQuery({
    queryKey: [chosenOrg, "", "whoami"],
    queryFn: () => client.get<{ scopes?: string[] }>("/auth/whoami", { org: chosenOrg }),
    enabled: Boolean(chosenOrg),
  });
  const [unchecked, setUnchecked] = React.useState<string[]>([]);
  const [days, setDays] = React.useState("90");
  const [done, setDone] = React.useState<"approved" | "denied">();

  if (!id || (auth.error instanceof ApiError && (auth.error.code === 404 || auth.error.code === 410))) {
    return <AuthCard title="This request has expired" description="Start again from your agent." />;
  }
  if (done === "denied") return <AuthCard title="Access denied" description="You can close this tab." />;
  if (done === "approved") {
    return (
      <AuthCard title="Connected" description={`Return to ${auth.data?.client.name ?? "your agent"}.`}>
        <ActionLink href={`/${encodeURIComponent(chosenOrg)}/integrations/mcp`}>See connected agents</ActionLink>
      </AuthCard>
    );
  }
  if (!auth.data) return <AuthCard title="Loading the request…" />;

  const a = auth.data;
  const have = who.data?.scopes ?? [];
  const granted = a.requestedScopes.filter((s) => allowedBy(have, s) && !unchecked.includes(s));
  const noneAllowed = who.isSuccess && a.requestedScopes.every((s) => !allowedBy(have, s));

  async function decide(decision: "approve" | "deny") {
    const r = await api.decideAgentAuthorization(id, {
      decision,
      ...(decision === "approve" ? { org: chosenOrg, scopes: granted, expiresInDays: Number(days), ...(projects === "all" ? {} : { project: projects }) } : {}),
    });
    setDone(decision === "approve" ? "approved" : "denied");
    if (r.redirectTo) window.location.assign(r.redirectTo);
  }

  return (
    <AuthCard title={`${a.client.name} wants to use Nodus`} description={<>Redirects to <code className="font-mono text-fg-body">{a.client.redirectHost}</code></>}>
      <div className="grid gap-5">
        {!a.client.verified ? (
          <Callout tone="warning">Nodus has not verified this client. Only continue if you started this from {a.client.name}.</Callout>
        ) : null}
        <div className="grid gap-1.5">
          <Label htmlFor="consent-org">Organization</Label>
          <Select id="consent-org" value={chosenOrg} onValueChange={setOrg} options={(orgs.data ?? []).map((o) => ({ value: o.metadata.name, label: o.spec?.displayName ?? o.metadata.name }))} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="consent-projects">Projects</Label>
          <Select id="consent-projects" value={projects} onValueChange={setProjects} options={[{ value: "all", label: "All projects" }, { value: "default", label: "default" }]} />
        </div>
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-ui font-medium text-fg-body">Access</legend>
          {a.requestedScopes.map((s) => {
            const allowed = allowedBy(have, s);
            const inputId = `scope-${s}`;
            return (
              <div key={s} className="flex items-start gap-2">
                <Checkbox
                  id={inputId}
                  disabled={!allowed}
                  checked={allowed && !unchecked.includes(s)}
                  onCheckedChange={(v) => setUnchecked((u) => (v === true ? u.filter((x) => x !== s) : [...u, s]))}
                />
                <Label htmlFor={inputId} className="font-normal">
                  {describeScope(s)} · <code className="font-mono text-small">{s}</code>
                  {!allowed ? <span className="block text-small text-fg-muted">Your role doesn&apos;t include this</span> : null}
                </Label>
              </div>
            );
          })}
        </fieldset>
        <div className="grid gap-1.5">
          <Label htmlFor="consent-expiry">Access lasts</Label>
          <Select id="consent-expiry" value={days} onValueChange={setDays} options={[{ value: "30", label: "30 days" }, { value: "90", label: "90 days" }, { value: "365", label: "1 year" }]} />
        </div>
        {noneAllowed ? <Callout tone="danger">Your role doesn&apos;t include any of the requested access. Ask an Owner, or deny the request.</Callout> : null}
        <div className="flex gap-2">
          {!noneAllowed ? <Button disabled={granted.length === 0} onClick={() => void decide("approve")}>Approve</Button> : null}
          <Button variant="outline" onClick={() => void decide("deny")}>Deny</Button>
        </div>
      </div>
    </AuthCard>
  );
}
